/**
 * Toast — sistema de notificações não-bloqueantes
 * Substitui todos os alert() do sistema
 *
 * Uso:
 *   import { useToast } from '../components/Toast';
 *   const toast = useToast();
 *   toast.success('Salvo com sucesso');
 *   toast.error('Erro ao salvar');
 *   toast.info('Processando...');
 *   toast.warning('Atenção: limite próximo');
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { theme } from '../styles/theme';

const ToastContext = createContext(null);

const ICONS = {
  success: <CheckCircle size={16} />,
  error:   <XCircle size={16} />,
  warning: <AlertTriangle size={16} />,
  info:    <Info size={16} />,
};

const COLORS = {
  success: { bg: theme.colors.successLight, border: theme.colors.successBorder, color: '#065f46', icon: theme.colors.success },
  error:   { bg: theme.colors.dangerLight,  border: theme.colors.dangerBorder,  color: '#991b1b', icon: theme.colors.danger },
  warning: { bg: theme.colors.warningLight, border: theme.colors.warningBorder, color: '#92400e', icon: theme.colors.warning },
  info:    { bg: theme.colors.primaryLight, border: theme.colors.primaryBorder, color: '#1a49ad', icon: theme.colors.primary },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  const add = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++counter.current;
    setToasts(prev => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
    }
    return id;
  }, []);

  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = {
    success: (msg, d) => add(msg, 'success', d),
    error:   (msg, d) => add(msg, 'error', d),
    warning: (msg, d) => add(msg, 'warning', d),
    info:    (msg, d) => add(msg, 'info', d),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer>
        {toasts.map(t => (
          <ToastItem key={t.id} $type={t.type}>
            <ToastIcon $color={COLORS[t.type].icon}>{ICONS[t.type]}</ToastIcon>
            <ToastMsg>{t.message}</ToastMsg>
            <ToastClose onClick={() => remove(t.id)}><X size={14} /></ToastClose>
          </ToastItem>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

// ── Styled ────────────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(100%); }
  to   { opacity: 1; transform: translateX(0); }
`;

const ToastContainer = styled.div`
  position: fixed;
  bottom: ${theme.spacing[6]};
  right: ${theme.spacing[6]};
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  max-width: 360px;
  pointer-events: none;
  @media (max-width: 639px) {
    bottom: env(safe-area-inset-bottom, 12px);
    right: ${theme.spacing[3]};
    left: ${theme.spacing[3]};
    max-width: 100%;
  }
`;

const ToastItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.radii.lg};
  border: 1px solid ${p => COLORS[p.$type].border};
  background: ${p => COLORS[p.$type].bg};
  color: ${p => COLORS[p.$type].color};
  box-shadow: ${theme.shadows.lg};
  animation: ${slideIn} 200ms ease;
  pointer-events: all;
  font-size: ${theme.typography.sm};
  line-height: 1.4;
`;

const ToastIcon = styled.div`
  color: ${p => p.$color};
  flex-shrink: 0;
  margin-top: 1px;
`;

const ToastMsg = styled.div`flex: 1;`;

const ToastClose = styled.button`
  background: none; border: none; cursor: pointer;
  color: inherit; opacity: 0.6; padding: 0; flex-shrink: 0;
  display: flex; align-items: center;
  &:hover { opacity: 1; }
`;
